import { classifyFileName } from './editor-utils';
import type { AssetKind, StudioAsset } from './types';

export interface MediaMetadata {
  kind: AssetKind;
  width?: number;
  height?: number;
  duration?: number;
}

export function extractMediaMetadata(file: File): Promise<MediaMetadata> {
  const kind = classifyFileName(file.name, file.type);
  if (kind === 'image') return extractImageMetadata(file);
  if (kind === 'video') return extractVideoMetadata(file);
  if (kind === 'glb' || kind === 'gltf') return Promise.resolve({ kind });
  return Promise.resolve({ kind });
}

function extractImageMetadata(file: File): Promise<MediaMetadata> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ kind: 'image', width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to decode image: ${file.name}`));
    };
    img.src = url;
  });
}

function extractVideoMetadata(file: File): Promise<MediaMetadata> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({
        kind: 'video',
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
      URL.revokeObjectURL(url);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to decode video: ${file.name}`));
    };
    video.src = url;
  });
}

export async function createAssetFromFile(file: File): Promise<StudioAsset> {
  const url = URL.createObjectURL(file);
  const metadata = await extractMediaMetadata(file);
  const isModel = metadata.kind === 'glb' || metadata.kind === 'gltf';
  const modelMime = metadata.kind === 'glb' ? 'model/gltf-binary' : 'model/gltf+json';
  const dataUrl = isModel ? await fileToDataUrl(file, modelMime) : undefined;
  const thumbUrl = metadata.kind === 'video' ? await makeVideoThumbnail(url) : undefined;
  return {
    id: `asset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    name: file.name,
    kind: metadata.kind,
    mimeType: file.type,
    size: file.size,
    url,
    dataUrl,
    thumbUrl: thumbUrl ?? undefined,
    width: metadata.width,
    height: metadata.height,
    duration: metadata.duration,
    createdAt: Date.now(),
  };
}

export async function fileToDataUrl(file: File, mimeTypeOverride?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    if (mimeTypeOverride && !file.type) {
      reader.readAsDataURL(new Blob([file], { type: mimeTypeOverride }));
    } else {
      reader.readAsDataURL(file);
    }
  });
}

export async function loadImageBitmap(url: string): Promise<ImageBitmap> {
  const response = await fetch(url);
  const blob = await response.blob();
  return createImageBitmap(blob);
}

export async function makeVideoThumbnail(url: string, time = 0): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.src = url;
    video.currentTime = time;
    video.onloadeddata = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(video, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        resolve(null);
      }
    };
    video.onerror = () => resolve(null);
  });
}
